import { Badge } from '@apps/web/components/ui/badge';
import { Label } from '@apps/web/components/ui/label';

// The admin sync-settings response types `provider_settings` as an open
// object, so the generated API types cannot describe this payload. These
// mirror `GenerationsRolloutPreflight` in
// `libraries/python/data_source_accounts/generations_rollout.py`, which
// projects the canonical Generations capability registry.
export type GenerationsRolloutBlocker = {
    readonly code: string;
    readonly detail: string;
};

export type GenerationsRolloutCapability = {
    readonly key: string;
    readonly label: string;
    readonly repository_certification: 'hosted_certified' | 'not_certified';
    readonly enabled: boolean;
    readonly limitation: string | null;
};

export type GenerationsRolloutPreflight = {
    readonly ready: boolean;
    readonly blockers: ReadonlyArray<GenerationsRolloutBlocker>;
    readonly capabilities: ReadonlyArray<GenerationsRolloutCapability>;
};

type GenerationsRolloutPreflightReportProps = {
    readonly preflight: GenerationsRolloutPreflight | null;
};

/**
 * Display-only rollout posture for one Generations account.
 *
 * It renders what the server already derived and never enables a gate,
 * saves settings, or contacts Generations.
 */
export function GenerationsRolloutPreflightReport({
    preflight,
}: GenerationsRolloutPreflightReportProps) {
    if (preflight === null) {
        return null;
    }

    return (
        <div className="bg-muted/50 space-y-3 rounded-md border p-3">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <Label>Customer rollout preflight</Label>
                    <p className="text-muted-foreground text-xs">
                        Read-only report of the stored account. It does not
                        enable this account or contact Generations.
                    </p>
                    <p className="text-muted-foreground text-xs">
                        Hosted test evidence is not customer-specific
                        validation, certification, or approval.
                    </p>
                </div>
                <Badge variant="outline">
                    {preflight.ready ? 'Ready' : 'Not ready'}
                </Badge>
            </div>
            {preflight.blockers.length > 0 && (
                <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs">
                    {preflight.blockers.map((blocker) => (
                        <li key={blocker.code}>{blocker.detail}</li>
                    ))}
                </ul>
            )}
            <details className="text-xs">
                <summary className="cursor-pointer font-medium">
                    Capability evidence
                </summary>
                <div className="mt-2 space-y-2">
                    {preflight.capabilities.map((capability) => (
                        <div
                            key={capability.key}
                            className="flex items-start justify-between gap-4"
                        >
                            <div>
                                <p>{capability.label}</p>
                                {capability.limitation !== null && (
                                    <p className="text-muted-foreground">
                                        {capability.limitation}
                                    </p>
                                )}
                            </div>
                            <div className="flex shrink-0 gap-1">
                                <Badge variant="outline">
                                    {capability.repository_certification ===
                                    'hosted_certified'
                                        ? 'Hosted test evidence'
                                        : 'Not certified'}
                                </Badge>
                                <Badge variant="outline">
                                    {capability.enabled ? 'On' : 'Off'}
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
            </details>
        </div>
    );
}
